import { useLang } from '@/hooks/useLang';

const copy = {
  EN: {
    tag: 'AI Capabilities',
    title: 'Built on intelligence.',
    sub: 'Every production is powered by a stack of AI tools working in harmony.',
    capabilities: [
      {
        icon: 'ri-eye-line',
        title: 'AI-Generated Visuals',
        desc: 'Photorealistic and stylized visuals generated entirely by AI — no camera required.',
      },
      {
        icon: 'ri-translate-2',
        title: 'Multi-Language Voice',
        desc: 'Voice generation in 175+ languages with native-level quality.',
      },
      {
        icon: 'ri-stack-line',
        title: 'Scalable Production',
        desc: 'From a single video to an entire content library — scale without limits.',
      },
      {
        icon: 'ri-flashlight-line',
        title: 'Fast Turnaround',
        desc: 'What used to take weeks now takes days. AI accelerates every stage of production.',
      },
      {
        icon: 'ri-user-heart-line',
        title: 'Hybrid AI + Creative',
        desc: 'AI handles the heavy lifting. Human creatives shape the soul of every project.',
      },
    ],
    visual: {
      label: 'Production Engine',
      status: 'Active',
    },
  },
  TR: {
    tag: 'Yapay Zeka Yetenekleri',
    title: 'Zeka üzerine inşa edildi.',
    sub: 'Her prodüksiyon, uyum içinde çalışan yapay zeka araçlarıyla desteklenir.',
    capabilities: [
      {
        icon: 'ri-eye-line',
        title: 'Yapay Zeka Görselleri',
        desc: 'Kamera gerektirmeden tamamen yapay zeka tarafından üretilen gerçekçi görseller.',
      },
      {
        icon: 'ri-translate-2',
        title: 'Çok Dilli Seslendirme',
        desc: '175+ dilde anadil kalitesinde ses üretimi.',
      },
      {
        icon: 'ri-stack-line',
        title: 'Ölçeklenebilir Prodüksiyon',
        desc: 'Tek bir videodan tüm içerik kütüphanesine — sınırsız ölçeklendirme.',
      },
      {
        icon: 'ri-flashlight-line',
        title: 'Hızlı Teslimat',
        desc: 'Haftalar süren süreçler artık günler içinde tamamlanıyor.',
      },
      {
        icon: 'ri-user-heart-line',
        title: 'Hibrit Yapay Zeka + Yaratıcılık',
        desc: 'Yapay zeka ağır işi üstlenir. İnsan yaratıcılar her projeye ruh katar.',
      },
    ],
    visual: {
      label: 'Prodüksiyon Motoru',
      status: 'Aktif',
    },
  },
};

export default function TechnologySection() {
  const lang = useLang();
  const t = copy[lang];

  return (
    <section id="technology" className="py-32 bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-8 md:px-16">
        <div className="flex flex-col lg:flex-row gap-20 items-start">
          {/* Left */}
          <div className="flex-1">
            <span className="text-xs font-semibold text-gray-400 tracking-widest uppercase mb-3 block">
              {t.tag}
            </span>
            <h2
              className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {t.title}
            </h2>
            <p className="text-gray-400 text-base leading-relaxed mb-12 max-w-sm">{t.sub}</p>

            {/* Capabilities list */}
            <div className="flex flex-col gap-6">
              {t.capabilities.map((cap, idx) => (
                <div key={idx} className="flex items-start gap-5 group">
                  <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-900 group-hover:border-gray-900 transition-all duration-300">
                    <i
                      className={`${cap.icon} text-gray-400 text-base w-4 h-4 flex items-center justify-center group-hover:text-white transition-colors duration-300`}
                    ></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-1">{cap.title}</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">{cap.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Uploaded custom visual */}
          <div className="flex-1 w-full">
            <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-white border border-gray-100">
              <img
                src="https://static.readdy.ai/image/5f8ccebbf933f3139676e12de0a0cc44/733eeea2c6a684bc5b805b11819f2cf9.jpeg"
                alt="KODO AI Technology"
                className="w-full h-full object-cover object-top"
              />
              {/* Status badge only — no stat boxes */}
              <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                <div className="bg-white/90 backdrop-blur-md rounded-full px-4 py-2 border border-gray-100">
                  <span className="text-xs font-semibold text-gray-600">{t.visual.label}</span>
                </div>
                <div className="bg-white/90 backdrop-blur-md rounded-full px-3 py-2 border border-gray-100 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block"></span>
                  <span className="text-xs font-semibold text-gray-600">{t.visual.status}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
